"use client";

import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import sideNavLogo from "@/assets/nimixa-icons/nimixa-logo-horizontal.png";
import brandFrameLogo from "@/assets/nimixa-icons/nimixa-mark.png";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Separator } from "@/components/ui/separator";

import ChevronUpIcon from "@/assets/icons/chevron-up.svg?react";
import ChevronDownIcon from "@/assets/icons/chevron-down.svg?react";
import LogoutIcon from "@/assets/icons/logout-icon.svg?react";
import ChevronRightIcon from "@/assets/icons/chevron-right.svg?react";

import LogoutDialog from "@/components/custom/LogoutDialog";

import { useGetMeQuery } from "@/features/profile/infrastructure/api/ProfileApi";
import { authSession } from "@/core/api/authSession";

import { CircleHelp, Info, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { getNavigationItems } from "./navigationItems";
import { textStyles } from "@/lib/textStyles";

const RAIL_WIDTH = 80;
const EXPANDED_WIDTH = 240;
const HOVER_COLLAPSE_DELAY_MS = 120;

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const Sidebar = () => {
  const { t } = useTranslation();
  const navigationItems = getNavigationItems(t);

  const [isHovered, setIsHovered] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [openLogout, setOpenLogout] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDropdownOpenRef = useRef(isDropdownOpen);

  const navigate = useNavigate();
  const location = useLocation();

  const { data: profile } = useGetMeQuery();

  const displayName = profile?.name ?? "";
  const displayRole = profile?.role ?? "";
  const initials = displayName ? getInitials(displayName) : "";

  const expanded = isHovered || isDropdownOpen;

  const clearCollapseTimer = () => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearCollapseTimer();
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    clearCollapseTimer();

    collapseTimerRef.current = setTimeout(() => {
      if (!isDropdownOpenRef.current) {
        setIsHovered(false);
      }
    }, HOVER_COLLAPSE_DELAY_MS);
  };

  useEffect(() => {
    isDropdownOpenRef.current = isDropdownOpen;
  }, [isDropdownOpen]);

  useEffect(() => {
    return () => clearCollapseTimer();
  }, []);

  return (
    <>
      <aside
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ width: expanded ? EXPANDED_WIDTH : RAIL_WIDTH }}
        className={cn(
          "relative z-20 flex h-screen shrink-0 flex-col overflow-hidden border-r bg-white",
          "transition-[width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
        )}
      >
        {/* Header */}
        <div className="relative flex h-16 shrink-0 items-center overflow-hidden">
          {/* Compact Logo (centered on the nav icon column) */}
          <div
            className={cn(
              "absolute left-5 flex h-9 w-9 items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
              expanded
                ? "scale-75 opacity-0"
                : "scale-100 opacity-100",
            )}
          >
            <img
              src={brandFrameLogo}
              alt="Nimixa"
              className="h-9 w-9 object-contain"
            />
          </div>

          {/* Full Logo */}
          <div
            className={cn(
              "absolute left-5 origin-left transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
              expanded
                ? "translate-x-0 scale-100 opacity-100"
                : "translate-x-[-12px] scale-95 opacity-0",
            )}
          >
            <img
              src={sideNavLogo}
              alt="Nimixa"
              className="h-9 w-auto max-w-none shrink-0"
            />
          </div>
        </div>

        {/* Menu */}
        <nav className="mt-2 flex flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden">
          {navigationItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");

            return (
              <button
                key={item.text}
                type="button"
                title={!expanded ? item.text : undefined}
                onClick={() => navigate(item.path)}
                className={cn(
                  "flex h-11 w-full cursor-pointer items-center border-r-[3px]",
                  "justify-start gap-3 px-[28px]",
                  "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                  isActive
                    ? "mr-[-3px] border-primary bg-[#EAF2FF] font-medium text-primary"
                    : "border-transparent text-gray-600 hover:bg-gray-100",
                )}
              >
                {/* Icon */}
                <span
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center",
                    "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                    isActive ? "text-primary" : "text-gray-500",
                  )}
                >
                  {item.icon}
                </span>

                {/* Text */}
                <span
                  className={cn(
                    "overflow-hidden whitespace-nowrap text-left text-sm",
                    "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                    expanded
                      ? "max-w-[160px] opacity-100"
                      : "max-w-0 opacity-0",
                  )}
                >
                  {item.text}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Profile */}
        <div className="w-full shrink-0 border-t">
          <DropdownMenu
            open={isDropdownOpen}
            onOpenChange={setIsDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title={!expanded ? displayName || t("profileMenu.profile") : undefined}
                className={cn(
                  "flex h-[77px] w-full cursor-pointer items-center hover:bg-gray-100",
                  "justify-start gap-3 px-[24px]",
                  "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                )}
              >
                {/* Avatar */}
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback
                    style={{ ...textStyles.body, fontWeight: 700 }}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>

                {/* Profile Info */}
                <div
                  className={cn(
                    "min-w-0 overflow-hidden text-left",
                    "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                    expanded
                      ? "max-w-[140px] opacity-100"
                      : "max-w-0 opacity-0",
                  )}
                >
                  <p className="truncate text-sm font-medium">
                    {displayName}
                  </p>

                  <p className="truncate text-xs text-gray-500">
                    {displayRole}
                  </p>
                </div>

                {/* Chevron */}
                <span
                  className={cn(
                    "ml-auto shrink-0 text-gray-400",
                    "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                    expanded
                      ? "w-4 opacity-100"
                      : "w-0 overflow-hidden opacity-0",
                  )}
                >
                  {isDropdownOpen ? (
                    <ChevronUpIcon className="h-4 w-4" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4" />
                  )}
                </span>
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              side="right"
              sideOffset={4}
              alignOffset={30}
              className="flex w-[266px] flex-col gap-0.5 rounded-xl border bg-white p-1"
            >
              {/* Profile Row */}
              <DropdownMenuItem
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate("/profile");
                }}
                className="flex cursor-pointer items-center justify-between rounded-md px-2 py-2"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback
                      style={{ ...textStyles.body, fontWeight: 700 }}
                    >
                      {initials}
                    </AvatarFallback>
                  </Avatar>

                  <div>
                    <p className="text-sm font-medium">{displayName}</p>
                    <p className="text-xs text-gray-500">{displayRole}</p>
                  </div>
                </div>

                <ChevronRightIcon
                  className="h-8 w-8"
                  style={{ width: "32px", height: "32px" }}
                />
              </DropdownMenuItem>

              <Separator className="my-0.5" />

              <DropdownMenuItem
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate("/settings");
                }}
                className="cursor-pointer gap-2 px-2 py-2 text-gray-700"
              >
                <Settings className="h-4 w-4 text-gray-500" />
                {t("profileMenu.settings")}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate("/faq");
                }}
                className="cursor-pointer gap-2 px-2 py-2 text-gray-700"
              >
                <CircleHelp className="h-4 w-4 text-gray-500" />
                {t("profileMenu.faq")}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  setIsDropdownOpen(false);
                  navigate("/about");
                }}
                className="cursor-pointer gap-2 px-2 py-2 text-gray-700"
              >
                <Info className="h-4 w-4 text-gray-500" />
                {t("profileMenu.about")}
              </DropdownMenuItem>

              <Separator className="my-0.5" />

              <DropdownMenuItem
                onClick={() => setOpenLogout(true)}
                variant="destructive"
                className="cursor-pointer gap-2 px-2 py-2 text-[#C62828] focus:bg-red-50 focus:text-[#C62828]"
              >
                <LogoutIcon className="h-5 w-5 shrink-0" />
                {t("profileMenu.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Profile Dialog */}
      <Dialog open={openProfile} onOpenChange={setOpenProfile}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("profileMenu.profile")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-center">
            <Avatar className="mx-auto h-16 w-16">
              <AvatarFallback style={{ ...textStyles.body, fontWeight: 700 }}>
                {initials}
              </AvatarFallback>
            </Avatar>

            <p className="font-semibold">{displayName}</p>

            <p className="text-sm text-gray-500">{displayRole}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Logout Dialog */}
      <LogoutDialog
        open={openLogout}
        onClose={() => setOpenLogout(false)}
        onConfirm={() => {
          setOpenLogout(false);
          authSession.clearTokens();
          navigate("/login", { replace: true });
        }}
      />
    </>
  );
};

export default Sidebar;
